import { useMemo, useCallback, useRef, useEffect } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { eventToAvi } from '@confluence/pubsub-client/entry-points/PubSubClient.ts';
import { getPubSubClient } from '@confluence/pubsub-client/entry-points/client.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { getEditorResolvedState } from '@confluence/comments-util/entry-points/stateResolver.ts';
import { getSortedMarkerRefs } from '@confluence/comments-util/entry-points/domUtils.ts';
import {
	useCommentsData,
	CommentActionType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	CommentType,
	isCommentResolved,
	categorizeComments,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { getProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import {
	getCommentsDataFromCache,
	type Comment,
	type CommentReply,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

import {
	InlineCommentPubSubEvents,
	InlineCommentSubscriptionEvents,
} from '../CommentPubSubEvents.ts';
import {
	createCommentsPubSubExperienceTracker,
	CommentsPubSubSource,
} from '../utils/commentsPubSubExperienceTracking.ts';
import { shouldSkipOwnPubSubEvent } from '../utils/pubsubEventGuards.ts';

type BasePubsubProps = {
	contentId: string;
	contentType?: string | null;
};

const getAuthorAccountId = (comment: Comment | CommentReply): string | undefined =>
	comment.author && 'accountId' in comment.author
		? (comment.author.accountId ?? undefined)
		: undefined;

const isThreadOpen = (comment: Comment | undefined): boolean =>
	!!comment && !isCommentResolved(comment);

export type PubSubProps = BasePubsubProps & {
	triggerAnalyticsEvent?:
		| ((
				eventName: string,
				extraData?: {
					[key: string]: any;
				},
		  ) => Promise<void>)
		| CreateUIAnalyticsEvent;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	/** @deprecated Retained as a no-op compatibility input for existing consumers. */
	storedCommentData?: any;
	/** @deprecated Retained as a no-op compatibility input for existing consumers. */
	setStoredCommentData?: (commentData: any) => void;
	addCommentInCache?: ({
		newComment,
		onError,
	}: {
		newComment: any;
		onError?: (error: Error) => void;
	}) => void;
	deleteCommentInCache?: ({
		commentId,
		onError,
	}: {
		commentId: string;
		onError?: (error: Error) => void;
	}) => void;
};

export const useEditorInlineCommentsPubSub = ({
	contentId,
	contentType,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	addCommentInCache,
	deleteCommentInCache,
}: PubSubProps): {
	register: () => void;
	unregister: () => void;
	onPublicationEvent: (event: string, payload: any) => Promise<void>;
	reloadMissedComments: () => Promise<void>;
} => {
	const pubSubResourceType = contentType || 'page';
	const pageMode = useGetPageMode();
	const subscriptions = useRef<{ delta: string; subscriberId: string }[]>([]);

	const [{ orderedActiveAnnotationIdList }, { updateRemovedCommentIdsMap, reopenParentComment }] =
		useCommentsData();
	const { userId: currentUserId } = useSessionData();
	const orderedActiveAnnotationIdListRef = useRef(orderedActiveAnnotationIdList);
	const { setContentId, setContentType, addSubscriber, removeSubscriber } =
		useSubscriptionsDispatchContext() || {};
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();

	useEffect(() => {
		orderedActiveAnnotationIdListRef.current = orderedActiveAnnotationIdList;
	}, [orderedActiveAnnotationIdList]);

	const resyncAnnotationState = useCallback(
		(markerRefs: string[]) => {
			const annotationIds = orderedActiveAnnotationIdListRef.current.map(
				(annotation) => annotation.threadKey,
			);

			if (triggerAnalyticsEvent) {
				// Updates ordered list of unresolved inline comments based on ADF to account for real-time changes (collaborative editing)
				void getEditorResolvedState({
					// the annotations won't already include the pubsub marker refs
					annotations: getSortedMarkerRefs([...annotationIds, ...markerRefs], 'edit'),
					pageId: contentId,
					triggerAnalyticsEvent,
					updateUnresolvedInlineComment,
				});
			}
		},
		[contentId, triggerAnalyticsEvent, updateUnresolvedInlineComment],
	);

	const handleOnPublicationEvent = useCallback(
		(event: string, markerRef: string) => {
			getEditorAnnotationEventEmitter().emit(event, markerRef);
			resyncAnnotationState([markerRef]);
		},
		[resyncAnnotationState],
	);

	const onPublicationEvent = useCallback(
		async (event: string, payload: any) => {
			if (!event) {
				return;
			}
			let inlineCommentRequestBody;
			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('cc_fps_migration_comments') &&
				fg('confluence_enable_subscriptions-context-provider')
			) {
				inlineCommentRequestBody = payload?.[event];
			} else {
				inlineCommentRequestBody = payload.inlineCommentRequestBody;
			}
			const { accountId } = payload || {};
			const { markerRef, commentId, inlineCommentType } = inlineCommentRequestBody || {};
			const isReply = inlineCommentType === 'REPLY';

			// Don't do anything when the current user performs an action, unless we need to process their reply.
			if (
				shouldSkipOwnPubSubEvent({
					accountId,
					currentUserId,
					isReply,
					isOwnReplyPubSubProcessingEnabled: expVal(
						'cc_comments_pubsub_process_own_replies',
						'isEnabled',
						false,
					),
				})
			) {
				return;
			}

			const {
				start: startCommentsPubSubExperience,
				succeed: succeedCommentsPubSubExperience,
				fail: failCommentsPubSubExperience,
			} = createCommentsPubSubExperienceTracker(CommentsPubSubSource.EditorInlineCommentsPubSub);

			startCommentsPubSubExperience(event);

			try {
				// These promises were previously dispatched as fire-and-forget (`void`).
				// Arguably they should be awaited, but until they are, we keep track of
				// their failures by collecting them here and awaiting `Promise.all` at
				// the end so any rejection becomes a `fail` on the experience and a
				// successful resolution becomes a `succeed`.
				const trackedPromises: Promise<unknown>[] = [];

				switch (event) {
					case InlineCommentSubscriptionEvents.InlineCommentUnresolved:
					case InlineCommentPubSubEvents.InlineCommentUnresolved:
						if (commentId) {
							// fetchCommentById implicitly updates useAllCommentsDataQuery cache
							await fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
						}

						// Remove from commentThreadsRemovedByOtherUsers when reopened
						if (markerRef && reopenParentComment) {
							reopenParentComment({
								threadKey: markerRef,
								commentType: CommentType.INLINE,
								parentCommentId: commentId,
							});
						}
						// Call handleOnPublicationEvent after store updates are complete
						handleOnPublicationEvent('unresolve', markerRef);
						break;
					case InlineCommentSubscriptionEvents.InlineCommentResolved:
					case InlineCommentPubSubEvents.InlineCommentResolved:
						handleOnPublicationEvent('resolve', markerRef);
						if (commentId) {
							// fetchCommentById implicitly updates useAllCommentsDataQuery cache
							trackedPromises.push(
								fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' }),
							);
						}

						const { inlineResolveProperties, inlineText } = inlineCommentRequestBody;

						if (inlineResolveProperties && inlineText) {
							updateRemovedCommentIdsMap({
								threadKey: markerRef,
								commentId,
								commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
								isReply,
								commentType: CommentType.INLINE,
							});
						}

						const { replies = [] } = inlineCommentRequestBody;

						if (updateUnreadCommentIDSet) {
							updateUnreadCommentIDSet(new Set([commentId, ...replies]), 'remove');
						}

						break;
					case InlineCommentSubscriptionEvents.InlineCommentCreated:
					case InlineCommentSubscriptionEvents.EditorInlineCommentCreated:
					case InlineCommentPubSubEvents.InlineCommentCreated:
					case InlineCommentPubSubEvents.EditorInlineCommentCreated:
						if (addCommentInCache && commentId) {
							const comment = await fetchCommentById({
								commentId,
								contentId,
								fetchPolicy: 'network-only',
							});
							if (comment) {
								addCommentInCache({ newComment: comment, onError: failCommentsPubSubExperience });
							}
						}

						if (updateUnreadCommentIDSet && accountId !== currentUserId) {
							updateUnreadCommentIDSet(new Set([commentId]), 'add');
						}

						// Call handleOnPublicationEvent after store updates are complete
						handleOnPublicationEvent('create', markerRef);
						break;
					case InlineCommentSubscriptionEvents.InlineCommentDeleted:
					case InlineCommentPubSubEvents.InlineCommentDeleted:
						// both a parent comment and reply can be deleted
						updateRemovedCommentIdsMap({
							threadKey: markerRef,
							commentId,
							isReply,
							commentActionType: CommentActionType.DELETE_COMMENT,
							commentType: CommentType.INLINE,
						});

						if (updateUnreadCommentIDSet) {
							updateUnreadCommentIDSet(new Set([commentId]), 'remove');
						}

						if (deleteCommentInCache && commentId) {
							deleteCommentInCache({ commentId, onError: failCommentsPubSubExperience });
						}

						if (!isReply && fg('confluence_frontend_new_dangling_comments_ux')) {
							handleOnPublicationEvent('delete', markerRef);
						}

						break;
					case InlineCommentSubscriptionEvents.InlineCommentUpdated:
					case InlineCommentPubSubEvents.InlineCommentUpdated:
						if (commentId) {
							// fetchCommentById implicitly updates useAllCommentsDataQuery cache
							trackedPromises.push(
								fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' }),
							);
						}

						break;
					default:
						failCommentsPubSubExperience(new Error(`Unhandled comments pubsub event: ${event}`));
						break;
				}

				await Promise.all(trackedPromises);
				succeedCommentsPubSubExperience();
			} catch (error: unknown) {
				failCommentsPubSubExperience(error);
				throw error;
			}
		},
		[
			contentId,
			handleOnPublicationEvent,
			updateRemovedCommentIdsMap,
			currentUserId,
			updateUnreadCommentIDSet,
			addCommentInCache,
			deleteCommentInCache,
			reopenParentComment,
		],
	);

	const register = useCallback(() => {
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
			if (!setContentType || !setContentId || !addSubscriber) {
				return;
			}
			setContentType(pubSubResourceType);
			setContentId(contentId);

			const subscribers = [
				{
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentResolved',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentResolved',
				},
				{
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentUnresolved',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentUnresolved',
				},
				{
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentCreated',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentCreated',
				},
				{
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentUpdated',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentUpdated',
				},
				{
					subscriberId: 'useEditorInlineCommentsPubSubEditorInlineCommentCreated',
					onSubscriptionData: onPublicationEvent,
					delta: 'editorInlineCommentCreated',
				},
			];

			for (const subscriber of subscribers) {
				subscriptions.current.push({
					subscriberId: addSubscriber(subscriber),
					delta: subscriber.delta,
				});
			}
			subscriptions.current.push({
				delta: 'inlineCommentDeleted',
				subscriberId: addSubscriber({
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentDeleted',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentDeleted',
				}),
			});
		} else {
			void getPubSubClient().then((client) => {
				client
					.joinChannel(pubSubResourceType, contentId)
					.finally(() => {
						void client.subscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('updated:inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('deleted:inline_comment'), onPublicationEvent);
					})
					.catch((error) => {
						throw new Error(`PubSub Web Client joinChannel ${JSON.stringify(error)}`);
					});
			});
		}
	}, [
		setContentType,
		pubSubResourceType,
		setContentId,
		contentId,
		onPublicationEvent,
		subscriptions,
		addSubscriber,
	]);

	const unregister = useCallback(() => {
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
			for (const subscriber of subscriptions.current) {
				removeSubscriber?.(subscriber);
			}
		} else {
			void getPubSubClient().then((client) => {
				void client.leaveChannel(pubSubResourceType, contentId);
				void client.unsubscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('updated:inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('deleted:inline_comment'), onPublicationEvent);
			});
		}
	}, [contentId, onPublicationEvent, pubSubResourceType, removeSubscriber, subscriptions]);

	const reloadMissedComments = useCallback(async (): Promise<void> => {
		const beforeData = getCommentsDataFromCache(contentId);
		await getProcessedCommentsDataState().refetch?.();
		const afterData = getCommentsDataFromCache(contentId);

		const contentStatusFilters = buildDefaultCommentsContentStatusFilters(pageMode);
		const categorize = (data: typeof afterData) =>
			categorizeComments(
				(data?.comments?.nodes ?? []).filter((comment): comment is Comment => !!comment),
				contentStatusFilters,
			);
		const after = categorize(afterData);

		if (!beforeData) {
			resyncAnnotationState(Object.keys(after.flatMarkerRefMap));
			return;
		}

		const before = categorize(beforeData);

		const knownCommentIds = new Set(Object.keys(before.flatCommentsMap));
		const unreadCommentIds = Object.keys(after.flatCommentsMap).filter(
			(commentId) =>
				!knownCommentIds.has(commentId) &&
				getAuthorAccountId(after.flatCommentsMap[commentId]) !== currentUserId,
		);

		if (updateUnreadCommentIDSet && unreadCommentIds.length) {
			updateUnreadCommentIDSet(new Set(unreadCommentIds), 'add');
		}

		const emitter = getEditorAnnotationEventEmitter();
		const changedMarkerRefs: string[] = [];

		for (const markerRef of new Set([
			...Object.keys(before.flatMarkerRefMap),
			...Object.keys(after.flatMarkerRefMap),
		])) {
			const isOpen = isThreadOpen(after.flatMarkerRefMap[markerRef]);

			if (isOpen === isThreadOpen(before.flatMarkerRefMap[markerRef])) {
				continue;
			}

			emitter.emit(isOpen ? 'unresolve' : 'resolve', markerRef);
			changedMarkerRefs.push(markerRef);
		}

		if (changedMarkerRefs.length) {
			resyncAnnotationState(changedMarkerRefs);
		}
	}, [contentId, pageMode, currentUserId, updateUnreadCommentIDSet, resyncAnnotationState]);

	return useMemo(
		() => ({
			register,
			unregister,
			onPublicationEvent,
			reloadMissedComments,
		}),
		[register, unregister, onPublicationEvent, reloadMissedComments],
	);
};
